/**
 * Password and Username Validation Utilities
 *
 * Centralized validation logic for username/password authentication.
 * These rules match the backend validation in the auth resolver.
 */

import { PasswordStrength } from '../types';

// Constants matching backend validation
export const PASSWORD_MIN_LENGTH = 8;
export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_PATTERN = /^[a-zA-Z0-9_]+$/;

/**
 * Validate password meets requirements
 * @param password - Password to validate
 * @returns Error message if invalid, null if valid
 */
export const validatePassword = (password: string): string | null => {
  if (!password) {
    return 'Password is required';
  }
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  }
  return null;
};

/**
 * Validate username meets requirements
 * @param username - Username to validate
 * @returns Error message if invalid, null if valid
 */
export const validateUsername = (username: string): string | null => {
  if (!username) {
    return 'Username is required';
  }
  if (username.length < USERNAME_MIN_LENGTH) {
    return `Username must be at least ${USERNAME_MIN_LENGTH} characters`;
  }
  if (!USERNAME_PATTERN.test(username)) {
    return 'Username can only contain letters, numbers, and underscores';
  }
  return null;
};

/**
 * Calculate password strength score
 * @param password - Password to analyze
 * @returns PasswordStrength object with score, label, and color
 */
export const getPasswordStrength = (password: string): PasswordStrength => {
  if (!password) {
    return { score: 0, label: 'weak', color: 'red.500' };
  }

  let score = 0;

  // Length checks
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;

  // Character variety checks
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++; // Mixed case
  if (/[0-9]/.test(password)) score++; // Has numbers
  if (/[^A-Za-z0-9]/.test(password)) score++; // Has special chars

  // Cap at 4 for our scale
  score = Math.min(score, 4);

  const labels: PasswordStrength['label'][] = ['weak', 'weak', 'fair', 'good', 'strong'];
  const colors = ['red.500', 'orange.500', 'yellow.500', 'green.400', 'green.500'];

  return {
    score,
    label: labels[score],
    color: colors[score],
  };
};

/**
 * Check if passwords match
 * @param password - Original password
 * @param confirmPassword - Confirmation password
 * @returns true if passwords match
 */
export const passwordsMatch = (password: string, confirmPassword: string): boolean => {
  return password === confirmPassword;
};

/**
 * Get password match error message
 * @param password - Original password
 * @param confirmPassword - Confirmation password
 * @returns Error message if passwords don't match, null if they do
 */
export const getPasswordMatchError = (password: string, confirmPassword: string): string | null => {
  if (!confirmPassword) {
    return null; // Don't show error for empty confirm field
  }
  if (!passwordsMatch(password, confirmPassword)) {
    return 'Passwords do not match';
  }
  return null;
};
